import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import api from './api';
import { getPhoto } from './photoDB';
import BackButton from './BackButton';

/** Maximum estimated decoded payload per batch (40 MB) */
const BATCH_SIZE_LIMIT = 40 * 1024 * 1024;

/**
 * Convert a Base64 data URL string into a Blob so it can be appended to
 * FormData as a proper binary file for multipart/form-data uploads.
 * Appending a plain string would leave request.files empty on the server.
 */
function dataURLtoBlob(dataURL) {
  const [header, data] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}

/**
 * Estimate the decoded byte size of a Base64 data URL.
 * Base64 encodes 3 bytes as 4 characters, so decoded size ≈ length × 0.75.
 */
function estimateBytes(dataURL) {
  return Math.ceil(dataURL.length * 0.75);
}

/**
 * Split an array of data URL strings into batches whose combined decoded
 * size stays under BATCH_SIZE_LIMIT, so no single request exceeds the
 * server's body limit.
 */
function batchBySize(photos) {
  const batches = [];
  let currentBatch = [];
  let currentSize = 0;

  for (const photo of photos) {
    const size = estimateBytes(photo);
    if (currentBatch.length > 0 && currentSize + size > BATCH_SIZE_LIMIT) {
      batches.push(currentBatch);
      currentBatch = [photo];
      currentSize = size;
    } else {
      currentBatch.push(photo);
      currentSize += size;
    }
  }
  if (currentBatch.length > 0) batches.push(currentBatch);
  return batches;
}

/**
 * Retry an async function up to maxAttempts times with exponential back-off.
 * Does NOT retry 401 (session expired - retrying can't help) or 502
 * (partial upload - a blind retry would duplicate the photos that DID
 * reach Asana/Azure).
 */
async function withRetry(fn, maxAttempts = 3) {
  const delays = [1000, 2000];
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const status = err.response?.status;
      const retryable = status !== 401 && status !== 502;
      if (retryable && attempt < maxAttempts - 1) {
        await new Promise(res => setTimeout(res, delays[attempt] ?? 2000));
      } else {
        throw err;
      }
    }
  }
}

/**
 * Archive a batch of photos to Azure Blob Storage via multipart upload.
 * Dealer identity travels in the session token, not the form.
 */
async function uploadPhotosToAzure(photos) {
  const formData = new FormData();
  photos.forEach((dataURL, i) => {
    formData.append('photos', dataURLtoBlob(dataURL), `photo_${i + 1}.jpg`);
  });
  const response = await api.post('/api/store-photos-azure', formData);
  return response.data;
}

function PhotoConfirm() {
  /*
  PhotoConfirm lets users review their selected photos, add comments, then
  submit concurrently to Azure Blob Storage (full-resolution archive for
  the art team) and Asana (workflow attachment).

  Large payloads are split into size-bounded batches; each batch is retried
  with back-off on transient failures. On success, navigates to /thank-you
  with allPhotoKeys so ThankYou can wipe the session photos from IndexedDB.
  */
  const location = useLocation();
  const navigate = useNavigate();

  // Full session key list - used for IndexedDB cleanup on success
  const photoKeys = useMemo(() => location.state?.photoKeys || [], [location.state]);
  // The subset the user selected in PhotoGallery
  const selectedPhotoKeys = useMemo(() => location.state?.selectedPhotoKeys || [], [location.state]);

  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [comments, setComments] = useState('');
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [transferMessage, setTransferMessage] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  // Load selected photos from IndexedDB once for the preview grid; the
  // same state is reused at submit time - no second IndexedDB round-trip.
  useEffect(() => {
    async function fetchSelectedPhotos() {
      const loaded = await Promise.all(selectedPhotoKeys.map(key => getPhoto(key)));
      setSelectedPhotos(loaded);
    }
    if (selectedPhotoKeys.length) fetchSelectedPhotos();
  }, [selectedPhotoKeys]);

  const handleConfirm = async () => {
    if (selectedPhotos.length === 0) return;

    setIsTransferring(true);
    setError('');

    try {
      const batches = batchBySize(selectedPhotos);

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const isFirst = i === 0;
        const batchLabel = batches.length > 1 ? ` (batch ${i + 1} of ${batches.length})` : '';
        setTransferMessage(`Transferring photos${batchLabel}...`);

        // Azure archive and Asana attachment run concurrently per batch.
        // Comments only ride the first batch to avoid duplicates in Asana.
        await withRetry(() => Promise.all([
          uploadPhotosToAzure(batch),
          api.post('/api/store-photos', { photos: batch, comments: isFirst ? comments : '' }),
        ]));
      }

      // Pass the full session key list so ThankYou can clear IndexedDB.
      navigate('/thank-you', { state: { allPhotoKeys: photoKeys, support: false } });
    } catch (err) {
      const status = err.response?.status;
      const serverMessage = err.response?.data?.message || err.response?.data?.error;
      if (status === 401) {
        setSessionExpired(true);
        setError('Your session has expired. Please re-enter your Dealer ID - your photos are saved and will still be here.');
      } else if (status === 502 && serverMessage) {
        setError(serverMessage);
      } else {
        setError('An error occurred while sending photos. Please try again.');
      }
      setIsTransferring(false);
      setTransferMessage('');
    }
  };

  const handleBack = () => {
    navigate('/photo-gallery', {
      state: { photoKeys, selectedPhotoKeys },
    });
  };

  return (
    <div className="PhotoConfirm">
      <h1 className="PhotoConfirm-title">Confirm Your Selected Photos</h1>
      <div className="PhotoConfirm-grid">
        {selectedPhotos.map((photo, index) => (
          <div key={index} className="PhotoConfirm-item">
            <img src={photo} alt={`Selected ${index + 1}`} className="PhotoConfirm-image" />
          </div>
        ))}
      </div>
      <textarea
        className="PhotoConfirm-comments"
        placeholder="Include any comments or questions you'd like to submit with your photos..."
        value={comments}
        onChange={e => setComments(e.target.value)}
        rows={3}
        maxLength={2000}
        style={{ width: '100%', margin: '20px 0', borderRadius: '8px', padding: '12px', fontSize: '16px', border: '1px solid #ccc' }}
      />
      {isTransferring && (
        <div style={{ color: '#1976d2', marginBottom: '12px', fontWeight: 500 }}>
          {transferMessage}
        </div>
      )}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>
          {error}
        </div>
      )}
      <div className="PhotoConfirm-actions">
        <button className="App-button" onClick={handleBack} disabled={isTransferring}>
          Back to Gallery
        </button>
        {sessionExpired ? (
          <button className="App-button" onClick={() => navigate('/dealer-id')}>
            Re-enter Dealer ID
          </button>
        ) : (
          <button className="App-button" onClick={handleConfirm} disabled={isTransferring || selectedPhotos.length === 0}>
            Confirm Photos
          </button>
        )}
      </div>
      <BackButton />
    </div>
  );
}

export default PhotoConfirm;
